export const calculateTotalSum = (transactions) => {
  const sum = transactions.reduce((total, transaction) => {
    return total + (transaction.amount || 0);
  }, 0);

  return parseFloat(sum.toFixed(2));
};

export const calculateTotalSubcharged = (transactions) => {
  const subchargedSum = transactions.reduce((total, transaction) => {
    const subchargedAmount =
      (transaction.amount || 0) - (transaction.tipAmount || 0);
    return total + subchargedAmount;
  }, 0);

  return parseFloat(subchargedSum.toFixed(2));
};

export const calculateTotalStripeFee = (transactions) => {
  const totalStripeFee = transactions.reduce((total, transaction) => {
    return total + (parseFloat(transaction.stripeFee) || 0);
  }, 0);

  return parseFloat(totalStripeFee.toFixed(2));
};

export const calculateTipsCollected = (transactions) => {
  const totalTips = transactions.reduce((total, transaction) => {
    return total + (parseFloat(transaction.tipAmount) || 0);
  }, 0);

  return parseFloat(totalTips.toFixed(2));
};

export const calculateDockRevenue = (transactions = []) => {
  return transactions.reduce((total, payment) => {
    const dockFee = typeof payment.dockFee === "number" ? payment.dockFee : 0;
    return parseFloat((total + dockFee).toFixed(2));
  }, 0);
};

export const calculateTotalCommissionCharged = (transactions) => {
  const totalCommission = transactions.reduce((total, transaction) => {
    return total + (transaction.commissionCharged || 0);
  }, 0);

  return parseFloat(totalCommission.toFixed(2));
};

export const getMonthlyData = (transactions) => {
  if (!Array.isArray(transactions) || transactions.length === 0) {
    return {
      data: Array(12).fill(0),
      years: Array(12).fill(new Date().getFullYear()),
    };
  }

  // Group transactions by year
  const yearWiseData = {};
  transactions.forEach((transaction) => {
    const txnDate = new Date(transaction.createdAt);
    const txnYear = txnDate.getFullYear();
    const txnMonth = txnDate.getMonth();

    if (!yearWiseData[txnYear]) {
      yearWiseData[txnYear] = Array(12).fill(0);
    }
    yearWiseData[txnYear][txnMonth] += 1;
  });

  // Identify available years
  const availableYears = Object.keys(yearWiseData)
    .map(Number)
    .sort((a, b) => b - a);
  const latestYear = availableYears[0]; // Most recent year (e.g., 2025)
  const previousYear = availableYears[1] || latestYear; // Previous year (e.g., 2024) or fallback

  // Initialize final arrays
  const finalMonthlyData = Array(12).fill(0);
  const monthYearMapping = Array(12).fill(latestYear); // Track the year for each month

  // Populate with latest year data first
  if (yearWiseData[latestYear]) {
    for (let i = 0; i < 12; i++) {
      finalMonthlyData[i] = yearWiseData[latestYear][i];
    }
  }

  // Fill missing months with previous year's data if available
  if (previousYear !== latestYear && yearWiseData[previousYear]) {
    for (let i = 0; i < 12; i++) {
      if (finalMonthlyData[i] === 0 && yearWiseData[previousYear][i] > 0) {
        finalMonthlyData[i] = yearWiseData[previousYear][i];
        monthYearMapping[i] = previousYear; // Assign the previous year's data
      }
    }
  }

  return { data: finalMonthlyData, years: monthYearMapping };
};

export const getDailyData = (transactions, startDate, endDate) => {
  const dailyCounts = {};
  const formattedLabels = [];

  const start = new Date(startDate);
  const end = new Date(endDate);

  // Loop through all days in the range
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const formattedDate = `${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(d.getDate()).padStart(2, "0")}`;
    dailyCounts[formattedDate] = 0;
    formattedLabels.push(formattedDate); // Store formatted dates
  }

  // Count transactions per day
  transactions.forEach((txn) => {
    const txnDate = new Date(txn.createdAt);
    const formattedDate = `${String(txnDate.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(txnDate.getDate()).padStart(2, "0")}`;

    if (dailyCounts[formattedDate] !== undefined) {
      dailyCounts[formattedDate] += 1;
    }
  });

  return { labels: formattedLabels, data: Object.values(dailyCounts) };
};

export const getWeeklyData = (transactions, targetMonth, targetYear) => {
  const weeklyData = {};
  transactions.forEach((transaction) => {
    const transactionDate = new Date(transaction.createdAt);
    if (
      transactionDate.getMonth() === targetMonth &&
      transactionDate.getFullYear() === targetYear
    ) {
      const dayOfMonth = transactionDate.getDate();
      const weekNumber = Math.ceil(dayOfMonth / 7);
      if (!weeklyData[weekNumber]) {
        weeklyData[weekNumber] = {};
      }
      if (!weeklyData[weekNumber][dayOfMonth]) {
        weeklyData[weekNumber][dayOfMonth] = 0;
      }
      weeklyData[weekNumber][dayOfMonth] += 1;
    }
  });

  // Generate a data array for Chart.js
  const weeklyCounts = [];
  const labels = Array.from({ length: 31 }, (_, i) => i + 1); // Days of the month (1-31)

  for (let week = 1; week <= 5; week++) {
    const weekData = labels.map((day) =>
      weeklyData[week] && weeklyData[week][day] ? weeklyData[week][day] : 0
    );
    weeklyCounts.push(weekData);
  }
  return {
    labels,
    datasets: weeklyCounts.map((data, index) => ({
      label: `Week ${index + 1}`,
      data,
      fill: false,
      borderColor: `rgba(75, 192, 192, ${0.5 + index * 0.1})`,
      tension: 0.1,
    })),
  };
};

export const calculateTeamTipStats = (transactions) => {
  const validTransactions = transactions.filter(
    (transaction) => !transaction.refundStatus
  );
  const teamTipTransactions = validTransactions.filter(
    (transaction) => transaction.tipType === "teamTip"
  );
  const numberOfTeamTips = teamTipTransactions.length;
  const percentageOfTeamTips =
    validTransactions.length > 0
      ? Math.round((numberOfTeamTips / transactions.length) * 100)
      : 0;
  return {
    numberOfTeamTipTransactions: numberOfTeamTips,
    percentageOfTeamTips,
  };
};

export const calculateIndividualTipStats = (transactions) => {
  const validTransactions = transactions.filter(
    (transaction) => !transaction.refundStatus
  );

  const individualTipTransactions = validTransactions.filter(
    (transaction) => transaction.tipType !== "teamTip"
  );

  const numberOfIndividualTips = individualTipTransactions.length;
  const percentageOfIndividualTips =
    validTransactions.length > 0
      ? Math.round((numberOfIndividualTips / transactions.length) * 100)
      : 0;

  return {
    numberOfIndividualTipTransactions: numberOfIndividualTips,
    percentageOfIndividualTips,
  };
};

export const calculateRefundedTransactionStats = (transactions) => {
  // Exclude refunded transactions from the total count
  const validTransactions = transactions.filter(
    (transaction) => !transaction.refundStatus
  );

  const refundedTransactions = transactions.filter(
    (transaction) => transaction.refundStatus === true
  );

  const numberOfRefundedTransactions = refundedTransactions.length;
  const percentageOfRefundedTransactions =
    transactions.length > 0
      ? Math.round((numberOfRefundedTransactions / transactions.length) * 100)
      : 0;

  return {
    numberOfRefundedTransactions,
    percentageOfRefundedTransactions,
  };
};

export const calculateMaidStats = (staffMembers) => {
  const maidMembers = staffMembers.filter((staff) => staff.type === "maid");
  const totalMaids = maidMembers.length;
  const totalStaff = staffMembers.length;

  const activeMaids = maidMembers.filter(
    (staff) => staff.chargesStatus === true
  ).length;
  const inactiveMaids = totalMaids - activeMaids;

  const percentageOfMaids =
    totalStaff > 0 ? Math.floor((totalMaids / totalStaff) * 100) : 0;

  return {
    totalMaids: totalMaids,
    percentageOfMaids: percentageOfMaids,
    activeMaids: activeMaids,
    inactiveMaids: inactiveMaids,
  };
};

export const calculateValetStats = (staffMembers) => {
  const valetMembers = staffMembers.filter((staff) => staff.type === "valet");
  const totalValets = valetMembers.length;
  const totalStaff = staffMembers.length;

  const activeValets = valetMembers.filter(
    (staff) => staff.chargesStatus === true
  ).length;
  const inactiveValets = totalValets - activeValets;

  const percentageOfValets =
    totalStaff > 0 ? Math.floor((totalValets / totalStaff) * 100) : 0;

  return {
    totalValets: totalValets,
    percentageOfValets: percentageOfValets,
    activeValets: activeValets,
    inactiveValets: inactiveValets,
  };
};

export const calculateBellmanStats = (staffMembers) => {
  const bellmanMembers = staffMembers.filter(
    (staff) => staff.type === "bellman"
  );
  const totalBellmen = bellmanMembers.length;
  const totalStaff = staffMembers.length;

  const activeBellmen = bellmanMembers.filter(
    (staff) => staff.chargesStatus === true
  ).length;
  const inactiveBellmen = totalBellmen - activeBellmen;

  const percentageOfBellmen =
    totalStaff > 0 ? Math.floor((totalBellmen / totalStaff) * 100) : 0;

  return {
    totalBellmen: totalBellmen,
    percentageOfBellmen: percentageOfBellmen,
    activeBellmen: activeBellmen,
    inactiveBellmen: inactiveBellmen,
  };
};

export const getVerifiedTeamTips = (valetTeams) => {
  return valetTeams.filter((team) => team.verified === true).length;
};

export const getUnverifiedTeamTips = (valetTeams) => {
  return valetTeams.filter((team) => team.verified === false).length;
};

// Creates an array of last 12 months labels + a map key "YYYY-MM"
export const buildLast12MonthBuckets = () => {
  const labels = [];
  const keys = [];
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}`;
    const label =
      d.toLocaleString("en-US", { month: "short" }) + " " + d.getFullYear();
    keys.push(key);
    labels.push(label);
  }
  return { labels, keys };
};
// Sum a numeric field per clientId
export const sumByClient = (txns, field) => {
  const map = new Map(); // clientId -> total
  for (const t of txns) {
    const id = t?.clientId;
    if (!id) continue;
    const val = Number(t?.[field] ?? 0);
    map.set(id, (map.get(id) || 0) + val);
  }
  return map;
};

export const countByClient = (txns) => {
  const map = new Map(); // clientId -> count
  for (const t of txns) {
    const id = t?.clientId;
    if (!id) continue;
    map.set(id, (map.get(id) || 0) + 1);
  }
  return map;
};
